import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, MapPin, Users, ArrowRight, QrCode, Share2, Sparkles, Scale, Lock, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import Logo from "../components/Logo";

export default function EventDetailPage() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState(null);
  const [openProfile, setOpenProfile] = useState(null); // intervenant object or null

  useEffect(() => {
    api.get(`/events/${slug}`).then((r) => setEv(r.data)).catch(() => setErr("Événement introuvable"));
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto p-12 text-center text-foreground/70">{err} — <Link to="/evenements" className="text-ink underline">retour</Link></div>;
  if (!ev) return <div className="p-12 text-foreground/60">Chargement…</div>;

  const link = `${window.location.origin}/i/${ev.slug}`;
  const share = (channel) => {
    const text = `Inscription à « ${ev.title} » — Front Uni pour la RDC`;
    if (channel === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + link)}`);
    else if (channel === "facebook") window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`);
    else if (channel === "email") window.location.href = `mailto:?subject=${encodeURIComponent(ev.title)}&body=${encodeURIComponent(text + " " + link)}`;
    else { navigator.clipboard.writeText(link); toast.success("Lien copié"); }
  };

  const fmtFull = (d) => new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const hasStages = (ev.stages?.length || 0) > 0;
  const intervenants = ev.intervenants || [];

  return (
    <div data-testid="event-detail-page">
      {/* BANNER — premium institutional */}
      <section className="bg-ink" data-testid="event-detail-page-hero" style={{ backgroundColor: '#0A2540', color: '#FFFFFF' }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-12 pt-12 pb-20 lg:pt-16 lg:pb-24">
          <Link to="/evenements" className="text-sm flex items-center gap-1.5 mb-8 hover:text-gold transition-colors" style={{ color: 'rgba(255,255,255,0.85)' }} data-testid="back-events">
            <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Tous les événements
          </Link>
          <div className="flex items-start gap-5">
            <Logo className="w-16 h-16 lg:w-20 lg:h-20 shrink-0 hidden sm:block" />
            <div className="min-w-0 flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm text-[0.7rem] uppercase tracking-[0.22em] font-bold" style={{ backgroundColor: '#D4AF37', color: '#0A2540' }}>
                <Sparkles className="w-3.5 h-3.5" /> {ev.is_featured ? "Événement principal" : "Événement"}
              </div>
              <h1 className="font-serif text-4xl lg:text-6xl mt-4 leading-[1.05] tracking-tight" style={{ color: '#FFFFFF' }}>{ev.title}</h1>
              <p className="mt-5 font-serif text-xl lg:text-2xl italic max-w-3xl pl-4" style={{ color: '#FFFFFF', borderLeft: '2px solid #D4AF37' }}>« {ev.theme} »</p>
            </div>
          </div>

          {/* Quick info row */}
          {!hasStages && (
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm" style={{ color: 'rgba(255,255,255,0.9)' }}>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4" style={{ color: '#D4AF37' }} /> {fmtFull(ev.date)} {ev.time && `· ${ev.time}`}</div>
              <div className="flex items-center gap-2">
                {ev.hide_location ? <><Lock className="w-4 h-4" style={{ color: '#D4AF37' }} /> Lieu communiqué après inscription</> :
                  <><MapPin className="w-4 h-4" style={{ color: '#D4AF37' }} /> {ev.city}, {ev.country}</>}
              </div>
              {intervenants.length > 0 && <div className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: '#D4AF37' }} /> {intervenants.length} intervenants</div>}
            </div>
          )}
        </div>
      </section>

      {/* STAGES timeline */}
      {hasStages && (
        <section className="bg-white border-b border-border" data-testid="stages-timeline">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
            <div className="eyebrow text-gold-dark mb-2 flex items-center gap-2">
              <Scale className="w-3.5 h-3.5" /> Étapes de la Croisade Scientifique
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-6">
              {ev.stages.map((s, i) => (
                <div key={i} className="bg-bone border border-border rounded-sm p-6 relative" data-testid={`stage-card-${i}`}>
                  <div className="absolute -top-3 left-6 w-10 h-10 grid place-items-center bg-ink text-gold font-serif text-lg rounded-sm shadow">{i + 1}</div>
                  <div className="mt-4">
                    {s.label && <div className="text-[0.7rem] uppercase tracking-[0.18em] font-semibold text-gold-dark">{s.label}</div>}
                    <h3 className="font-serif text-2xl text-ink mt-1">{s.city}</h3>
                    <div className="text-sm text-foreground/65">{s.country}</div>
                    <div className="mt-4 text-sm text-foreground/80 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gold-dark" />
                      <span>{fmtFull(s.date)}{s.time && ` · ${s.time}`}</span>
                    </div>
                    {s.location_note && (
                      <div className="mt-3 text-xs text-crimson italic flex items-start gap-1.5">
                        <Lock className="w-3 h-3 mt-0.5 shrink-0" /> {s.location_note}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DESCRIPTION + SIDEBAR INSCRIPTION */}
      <section className="py-20 bg-bone">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="eyebrow text-gold-dark">À propos de l'événement</div>
            <h2 className="font-serif text-3xl text-ink mt-3 mb-6 leading-tight">Programme</h2>
            <p className="text-foreground/80 leading-relaxed whitespace-pre-line text-[1.02rem] font-serif">{ev.description}</p>

            {/* INTERVENANTS */}
            {intervenants.length > 0 && (
              <div className="mt-14" data-testid="intervenants-section">
                <div className="eyebrow text-gold-dark">Intervenants</div>
                <h3 className="font-serif text-3xl text-ink mt-3 leading-tight">Panel scientifique</h3>
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  {intervenants.map((p, i) => {
                    const hasProfile = !!(p.bio || p.intervention_title);
                    return (
                      <div key={i}
                           className={`bg-white border border-border rounded-sm p-5 flex items-start gap-4 transition-colors ${hasProfile ? 'cursor-pointer hover:border-gold' : ''}`}
                           onClick={() => hasProfile && setOpenProfile(p)}
                           data-testid={`intervenant-${i}`}>
                        {p.photo ? (
                          <img src={p.photo} alt={p.name}
                               className="w-16 h-16 object-cover object-top rounded-sm shrink-0 border border-border"
                               loading="lazy" />
                        ) : (
                          <div className="w-16 h-16 grid place-items-center bg-ink text-gold font-serif text-xl rounded-sm shrink-0">
                            {p.name.split(/\s+/).filter((w) => /^[A-Z]/.test(w)).slice(-1)[0]?.[0] || "P"}
                          </div>
                        )}
                        <div className="min-w-0 pt-0.5">
                          <div className="font-serif text-ink text-base leading-tight">{p.name}</div>
                          {p.title && <div className="text-xs text-foreground/60 mt-1">{p.title}</div>}
                          {hasProfile && (
                            <div className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-gold-dark font-semibold"
                                 data-testid={`intervenant-${i}-view-profile`}>
                              Voir le profil <ArrowRight className="w-3 h-3" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Speakers fallback (text only) */}
            {!intervenants.length && ev.speakers?.length > 0 && (
              <div className="mt-10">
                <div className="eyebrow text-gold-dark">Intervenants</div>
                <ul className="mt-4 space-y-2">
                  {ev.speakers.map((s) => (
                    <li key={s} className="font-serif text-lg text-ink border-l-2 border-gold pl-4 py-1">{s}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* FUTURE GALLERY */}
            <div className="mt-14 bg-white border border-dashed border-border rounded-sm p-8 text-center">
              <div className="eyebrow text-gold-dark">Galerie</div>
              <p className="mt-2 text-sm text-foreground/65 font-serif italic">
                Les photos et vidéos de l'événement seront publiées ici à l'issue de chaque étape.
              </p>
            </div>
          </div>

          {/* SIDEBAR INSCRIPTION */}
          <aside className="lg:col-span-1">
            <div className="bg-white border border-border rounded-sm p-7 lg:sticky lg:top-24">
              <div className="flex items-center gap-3 mb-4">
                <Logo className="w-10 h-10 shrink-0" />
                <div>
                  <div className="eyebrow text-gold-dark text-[0.65rem]">Inscription officielle</div>
                  <div className="font-serif text-base text-ink leading-tight">Réservez votre place</div>
                </div>
              </div>
              <p className="text-foreground/65 text-sm leading-relaxed">
                Inscription gratuite et obligatoire. Le lieu exact vous sera communiqué après validation.
                Un numéro et un QR code de badge vous seront générés automatiquement.
              </p>
              <Link to={`/i/${ev.slug}`} className="btn-primary w-full justify-center mt-6" data-testid="event-register-cta">
                S'inscrire maintenant <ArrowRight className="w-4 h-4" />
              </Link>

              <div className="mt-7 pt-6 border-t border-border">
                <div className="eyebrow text-gold-dark mb-3">QR Code</div>
                <div className="bg-bone p-3 rounded-sm grid place-items-center">
                  {ev.qr_data_url ? <img src={ev.qr_data_url} alt="QR Code inscription" className="w-32 h-32" data-testid="event-qr" /> : <QrCode className="w-12 h-12 text-foreground/30" />}
                </div>
                <div className="mt-5">
                  <div className="eyebrow text-gold-dark mb-2">Partager</div>
                  <div className="flex flex-wrap gap-2">
                    {[["whatsapp", "WhatsApp"], ["facebook", "Facebook"], ["email", "Email"], ["copy", "Copier"]].map(([k, l]) => (
                      <button key={k} onClick={() => share(k)} className="px-3 py-1.5 text-xs border border-border hover:border-ink rounded-sm transition-colors flex items-center gap-1.5" data-testid={`share-${k}`}>
                        <Share2 className="w-3 h-3" /> {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-border text-[0.7rem] text-foreground/55 leading-relaxed">
                  Inscriptions obligatoires<br />
                  <span className="text-ink font-semibold">+32 470 43 46 43</span> · <span className="text-ink font-semibold">+33 7 45 15 86 00</span><br />
                  contact@frontunirdc.be
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* PROFILE MODAL */}
      {openProfile && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4 sm:p-6 animate-fade-up"
             data-testid="intervenant-profile-modal"
             onClick={() => setOpenProfile(null)}>
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" />
          <div className="relative bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl rounded-sm"
               onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpenProfile(null)}
                    aria-label="Fermer"
                    data-testid="intervenant-profile-close"
                    className="absolute top-3 right-3 w-9 h-9 grid place-items-center text-ink/70 hover:text-ink hover:bg-bone rounded-sm transition-colors z-10">
              <X className="w-5 h-5" />
            </button>
            <div className="grid sm:grid-cols-12 gap-0">
              {openProfile.photo && (
                <div className="sm:col-span-4 bg-ink">
                  <img src={openProfile.photo} alt={openProfile.name}
                       className="w-full h-full object-cover object-top sm:min-h-[260px] aspect-[3/4] sm:aspect-auto" />
                </div>
              )}
              <div className={`${openProfile.photo ? 'sm:col-span-8' : 'sm:col-span-12'} p-7 sm:p-9`}>
                <div className="eyebrow text-gold-dark">Intervenant</div>
                <h3 className="font-serif text-2xl sm:text-3xl text-ink mt-2 leading-tight">{openProfile.name}</h3>
                {openProfile.title && <div className="text-sm text-foreground/65 mt-1">{openProfile.title}</div>}
                <div className="divider-gold w-16 mt-5" />

                {openProfile.intervention_title && (
                  <div className="mt-6">
                    <div className="eyebrow text-gold-dark">Thème de l'intervention</div>
                    <p className="font-serif text-base text-ink mt-2 leading-snug">
                      {openProfile.intervention_title}
                    </p>
                  </div>
                )}

                {openProfile.research_question && (
                  <div className="mt-5 border-l-2 border-gold pl-4 py-1">
                    <div className="eyebrow text-foreground/60 text-[0.65rem]">Question de recherche</div>
                    <p className="text-sm text-foreground/80 mt-1.5 italic leading-relaxed font-serif">
                      « {openProfile.research_question} »
                    </p>
                  </div>
                )}

                {openProfile.bio && (
                  <div className="mt-6">
                    <div className="eyebrow text-gold-dark">Résumé du profil</div>
                    <p className="mt-2 text-[0.95rem] text-foreground/85 leading-relaxed whitespace-pre-line">
                      {openProfile.bio}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
